import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap, map, mapTo } from 'rxjs/operators';

import { MarketplaceStore } from './marketplace.store';
import { MarketplaceQuery } from './marketplace.query';
import { ApiService } from '../../shared/services/core/api.service';
import { LoggingService } from '../../shared/services/core/logging.service';
import { BasketService } from './basket.service';
import { MarketplaceItem } from './models/marketplace-item.model';
import { MarketplaceBasketItem } from './models/basket/marketplace-basket-item.model';
import { MpThemeService } from './mp-theme.service';
import { PurchaseStore } from '../../loyalty-history/purchase/purchase.store';
import { MarketplaceBasketCustomFieldModel } from './models/basket/marketplace-basket-customfield.model';
import { ProviderUiActionResponse } from '../../ecash/models/provider-ui-action-response.model';
import { MarketplacePurchaseBasketModel } from './models/marketplace-purchase-basket.model';
import { MemberDetails } from '../../account/member/models/member-details.model';
import { withTransaction } from '@datorama/akita';

@Injectable({ providedIn: 'root' })
export class MarketplaceService {
  private marketplaceStore = inject(MarketplaceStore);
  private marketplaceQuery = inject(MarketplaceQuery);
  private purchaseStore = inject(PurchaseStore);
  private api = inject(ApiService);
  private logService = inject(LoggingService);
  private basketService = inject(BasketService);
  private themeService = inject(MpThemeService);

  /** Fetches the marketplace items and puts them in the store. */
  fetchMarketplaceItems(): Observable<null> {
    this.marketplaceStore.setLoading(true);

    const request$: Observable<null> = this.api.get<MarketplaceItem>('marketplace/items').pipe(
      map((items) => items.map((item) => ({ ...item, theme: this.themeService.getTheme(item.ProcessProviderId) }))),
      tap((items) => this.marketplaceStore.set(items)),
      mapTo(null)
    );
    return request$;
  }

  /** Fetches the marketplace items with tags like Events,Experience & Marketplace and puts them in the store. */
  fetchMarketplaceItemsbyTag(tags: any): Observable<any> {
    this.marketplaceStore.setLoading(true);

    const request$: Observable<any> = this.api.get<MarketplaceItem>('marketplace/items?tags=' + tags.toString()).pipe(
      map((items) => items.map((item) => ({ ...item, theme: this.themeService.getTheme(item.ProcessProviderId) }))),
      tap((items) => this.marketplaceStore.set(items))
    );
    return request$;
  }

  /** Fetches a specific marketplace item (ignoring cache) and puts it in the store. */
  fetchMarketplaceItemById(id: number): Observable<null> {
    this.marketplaceStore.setLoading(true);

    const request$: Observable<null> = this.api.getById<MarketplaceItem>('marketplace/items', id.toString()).pipe(
      map((item) => ({ ...item, theme: this.themeService.getTheme(item.ProcessProviderId) })),
      withTransaction((item) => {
        this.marketplaceStore.upsert(id, item);
      }),
      mapTo(null)
    );

    return request$;
  }

  /**
   * update store values when item is added to basket.
   */
  updateMarketplaceStoreWithBasketItem(itemId: number, quantity: number) {
    let item = this.marketplaceQuery.getEntity(itemId);
    if (!item) {
      return;
    }

    this.marketplaceStore.update(itemId, {
      AvailabilityCount: item.AvailabilityCount - quantity,
      AccountPurchaseCount: item.AccountPurchaseCount + quantity,
      MemberPurchaseCount: item.MemberPurchaseCount + quantity,
    });
  }

  removeBasketItemFromStore(itemId: number, quantity: number) {
    let item = this.marketplaceQuery.getEntity(itemId);
    if (!item) {
      return;
    }

    this.marketplaceStore.update(itemId, {
      AvailabilityCount: item.AvailabilityCount + quantity,
      AccountPurchaseCount: item.AccountPurchaseCount - quantity,
      MemberPurchaseCount: item.MemberPurchaseCount - quantity,
    });
  }

  /**
   * Purchase a single item.
   * @returns the full provider response
   */
  async purchaseItemWithProviderResopnse(
    itemId: number,
    quantity: number,
    attributeMappingId?: number,
    memberId?: string,
    customFields?: MarketplaceBasketCustomFieldModel[],
    useCardToken: boolean = false
  ): Promise<ProviderUiActionResponse | undefined> {
    const item = this.marketplaceQuery.getEntity(itemId);

    if (!item) {
      return;
    }

    this.logService.debug(`Purchasing ${quantity} quantity of marketplace item`, item);

    this.basketService.emptyBasket();
    this.basketService.addItemToBasket(
      MarketplaceBasketItem.fromMarketplaceItem(item, quantity, attributeMappingId, customFields)
    );

    return this.purchaseBasketWithProviderResponse(this.basketService.getCurrentBasket().items, memberId, useCardToken);
  }

  /**
   * Purchase a single item.
   * @returns The order ID or the purchase.
   */
  async purchaseItem(
    itemId: number,
    quantity: number,
    attributeMappingId?: number,
    memberId?: string,
    customFields?: MarketplaceBasketCustomFieldModel[],
    useCardToken: boolean = false
  ): Promise<string | undefined> {
    const item = this.marketplaceQuery.getEntity(itemId);

    if (!item) {
      return;
    }

    this.logService.debug(`Purchasing ${quantity} quantity of marketplace item`, item);

    this.basketService.emptyBasket();
    this.basketService.addItemToBasket(
      MarketplaceBasketItem.fromMarketplaceItem(item, quantity, attributeMappingId, customFields)
    );

    return this.purchaseBasket(this.basketService.getCurrentBasket().items, memberId, useCardToken);
  }

  /**
   * Purchase the items in the current basket.
   * @returns The order ID or the purchase.
   */
  async purchaseBasket(basket: MarketplaceBasketItem[], memberId?: string, useCardToken: boolean = false): Promise<string> {
    return (await this.purchaseBasketWithProviderResponse(basket, memberId, useCardToken)).OrderId;
  }

  /**
   * Purchase the items in the current basket.
   * @returns The full provider response
   */
  async purchaseBasketWithProviderResponse(
    basket: MarketplaceBasketItem[],
    memberId?: string,
    useCardToken: boolean = false
  ): Promise<ProviderUiActionResponse> {
    this.logService.debug(`Purchasing marketplace item basket`, basket);
    let response = await this.api
      .post<MarketplaceBasketItem[]>(
        'marketplace/Purchase' + (memberId ? '?memberId=' + memberId : '') + '?useCardToken=' + useCardToken,
        basket
      )
      .pipe(
        tap((response) => {
          if (response.Data.Success) {
            this.purchaseStore.setHasCache(false);
          }
        })
      )
      .toPromise();

    if (response.Data.Success) {
      this.basketService.emptyBasket();
    }
    return response.Data as ProviderUiActionResponse;
  }

  /**
   * This will download the marketplace purchased item
   * @param logId is the log id of the purchased item.
   */
  downloadMarketplaceItem(logId: number): Observable<Blob> {
    return this.api.getFile(`marketplace/download/${logId}`);
  }

  /**
   * This will disable cache of the marketpalce store, so
   * that a fetch can be made again.
   */
  disableMarketplaceStoreCache() {
    this.marketplaceStore.setHasCache(false);
  }

  /**
   * This endpoint is used to purchase marketplace items, it works with merchant and ecash.
   * This is to replace the api/purchase endpoint.
   */
  async purchaseBasketMarketplace(body: MarketplacePurchaseBasketModel) {
    this.logService.debug(`Purchasing marketplace item in the basket`, body);

    const response = await this.api
      .post('marketplace/purchasebasket', body)
      .pipe(
        tap((res) => {
          if (res.Data.Success) {
            this.purchaseStore.setHasCache(false);
          }
        })
      )
      .toPromise();

    return response;
  }

  /**
   * This endpoint is used to purchase marketplace items, it works with merchant and ecash.
   * This is to replace the api/purchase endpoint.
   */
  async completePurchaseBasketOrder(
    orderId: string,
    failureUrl: string,
    successUrl: string,
    useCardToken: boolean = true,
    saveAsCardToken: boolean = false,
    postBackUrl = ''
  ) {
    const body = {
      OrderId: orderId,
      FailureUrl: failureUrl,
      SuccessUrl: successUrl,
      PostBackUrl: postBackUrl,
      UseCardToken: useCardToken,
      SaveCardAsToken: saveAsCardToken,
    };

    this.logService.debug(`Purchasing marketplace item in the basket`, body);

    const response = await this.api.post('marketplace/checkout/complete', body).toPromise();

    return response;
  }

  /**
   * This endpoint is used to purchase marketplace items, it works with merchant and ecash.
   * This is to replace the api/purchase endpoint.
   */
  async checkoutStart(body: any) {
    return await this.api.post('marketplace/checkout/start', body).toPromise();
  }

  /**
   * This endpoint is used to update the shipping option of the basket
   */
  async updateAdress(orderId: string, memberDetails: MemberDetails) {
    return await this.api
      .post('marketplace/checkout/address' + (orderId ? '?orderId=' + orderId : ''), memberDetails)
      .toPromise();
  }

  /**
   * This endpoint is used to update the shipping option of the basket
   */
  async updateShippingAdress(orderId: string, memberDetails: MemberDetails) {
    return await this.api
      .post('marketplace/checkout/shipping' + (orderId ? '?orderId=' + orderId : ''), memberDetails)
      .toPromise();
  }

  /** Update the locked property on all marketplace items to true */
  lockAllItems() {
    this.marketplaceStore.update(null, { Locked: true });
  }
}
